/**
 * Table constructs, spelled the way the generator dispatches on them.
 *
 * These are not display labels. `get_model_template_for_construct` looks the stored value up
 * in a map keyed on the backend's DataMartConstruct enum; a value outside that set misses,
 * the KeyError is swallowed, and the entity is reported as "No SQL was produced — usually
 * missing column mappings", which is not the reason. This file used to send `fact_as_table`,
 * `SCD1_as_table` and `SCD2_as_table`, so every fact and dimension created here was born
 * ungeneratable.
 *
 * The backend normalises the old spellings and rejects anything it cannot dispatch, so these
 * two can be deployed in either order — but this is the side that has to be right.
 */
export const FACT_TABLE_CONSTRUCT_DEFAULT = "FactAsTable";

export const DIMENSION_TABLE_CONSTRUCT_OPTIONS = [
  { label: "SCD1", value: "SCD1AsTable" },
  // One option here, three variants in the backend. Every SCD2 dimension modelled on this
  // platform is the single-satellite kind.
  { label: "SCD1_V2", value: "SCD1_V2_as_table" },
  { label: "SCD2", value: "SCD2SingleSatAsTable" },
  { label: "SCD2_V2", value: "SCD2_V2_as_table"}
] as const;

export const DIMENSION_TABLE_CONSTRUCT_DEFAULT = DIMENSION_TABLE_CONSTRUCT_OPTIONS[0].value;

/**
 * The "_V2_" options are not constructs. Each means "this construct, rendered by the second
 * template version"; the backend stores the construct and sets use_second_template_version
 * (`_DM_SECOND_VERSION_CONSTRUCT_ALIASES` in app/api/dwa.py).
 */
const SECOND_VERSION_OPTION_BY_CONSTRUCT: Record<string, string> = {
  SCD1AsTable: "SCD1_V2_as_table",
  SCD2SingleSatAsTable: "SCD2_V2_as_table"
};

// Spellings older backends stored. The backend resolves them to the same construct.
const CONSTRUCT_BY_LEGACY_SPELLING: Record<string, string> = {
  scd1_as_table: "SCD1AsTable",
  scd2_as_table: "SCD2SingleSatAsTable"
};

export const isSecondTemplateVersionOption = (value: string): boolean =>
  Object.values(SECOND_VERSION_OPTION_BY_CONSTRUCT).includes(value);

/**
 * The dropdown value for a stored construct and its template-version flag. Constructs the
 * dropdown has no option for come back unchanged, so saving another field does not rewrite
 * them.
 */
export const toDimensionTableConstructOption = (
  tableConstruct: string,
  useSecondTemplateVersion: boolean
): string => {
  const construct = CONSTRUCT_BY_LEGACY_SPELLING[tableConstruct.toLowerCase()] ?? tableConstruct;
  if (!useSecondTemplateVersion) {
    return construct;
  }
  return SECOND_VERSION_OPTION_BY_CONSTRUCT[construct] ?? construct;
};
